import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setDepartmentFilter } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertCircle } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = m[1];

    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id });
        setDepartmentFilter("");        // siempre se entra en la vista consolidada
        setUser(r.data.user);
        // Clean URL
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
        const msg = `${status ? `[${status}] ` : ""}${detail}`;
        console.error("AuthCallback failed:", msg, e);
        setError(msg);
        // No auto-redirect — the user needs to see the error and act (log out
        // of Google, switch account). Auto-redirecting caused a loop where
        // Google kept re-authenticating with the same wrong account.
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="flex flex-col items-center gap-3 text-slate-600 max-w-md text-center">
        {error ? (
          <>
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm font-semibold text-slate-900">No pudimos iniciar tu sesión</p>
            <p className="text-xs text-red-600 break-all" data-testid="auth-error-message">{error}</p>
            {/* Common cause: Google auto-picked a personal account instead of
                the one linked to the platform. Give a clear escape hatch that
                signs the user out of Google FIRST so they can pick the right
                account on the next attempt (instead of looping on the same
                cached session). */}
            <p className="text-xs text-slate-500 mt-2 max-w-sm">
              Esto suele pasar cuando Google elige una cuenta de correo distinta a la que
              tienes registrada. Cierra sesión en Google e intenta de nuevo con el correo
              correcto.
            </p>
            <div className="flex flex-col gap-2 mt-3 w-full">
              <a
                href="https://accounts.google.com/Logout"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                data-testid="google-logout-link"
              >
                Cerrar sesión en Google
              </a>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="inline-flex justify-center items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="back-to-login-btn"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm">Autenticando…</p>
          </>
        )}
      </div>
    </div>
  );
}
